import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDailyBrief } from '@/hooks/useDailyBrief';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { Sparkles, RefreshCw, CalendarDays, CheckSquare, Cloud, Loader2, Car, Quote } from 'lucide-react';

interface DailyBriefWidgetProps {
  userId: string | null;
}

export const DailyBriefWidget: React.FC<DailyBriefWidgetProps> = ({ userId }) => {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { brief, loading } = useDailyBrief(userId);

  const handleRefresh = async () => {
    if (!userId) return;
    setIsRefreshing(true);
    try {
      await httpsCallable(getFunctions(getApp()), 'refreshDailyBrief')();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (userId === null) {
    return null;
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand" />
            <span className="font-black uppercase tracking-widest text-2xs text-white/50">{t('brief.title', 'Daily Brief')}</span>
          </div>
          <button disabled className="w-4 h-4 text-slate-500">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-slate-700 rounded animate-pulse"></div>
          <div className="h-3 bg-slate-700 rounded animate-pulse w-3/4"></div>
          <div className="h-3 bg-slate-700 rounded animate-pulse w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand" />
            <span className="font-black uppercase tracking-widest text-2xs text-white/50">{t('brief.title', 'Daily Brief')}</span>
          </div>
          <button disabled className="w-4 h-4 text-slate-500">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <p className="text-white/40 text-sm">{t('brief.generating', 'Your brief is being generated...')}</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand" />
          <span className="font-black uppercase tracking-widest text-2xs text-white/50">Daily Brief</span>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="w-4 h-4 text-slate-400 hover:text-white transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {brief.stoicQuote && (
        <div className="mb-3 p-3 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-start gap-2">
            <Quote className="w-3 h-3 text-indigo-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-white/70 italic leading-snug">"{brief.stoicQuote.text}"</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-white/40 mt-1">— {brief.stoicQuote.author}</p>
            </div>
          </div>
        </div>
      )}

      <p className="text-sm text-white/70 line-clamp-3 mb-4">{brief.narrative}</p>

      {brief.commuteEstimate && (
        <div className="flex items-center gap-2 mb-2">
          <Car className="w-3 h-3 text-slate-400" />
          <span className="text-xs text-white/60">{brief.commuteEstimate}</span>
        </div>
      )}

      {brief.homeWeather && (
        <div className="flex items-center gap-2 mb-2">
          <Cloud className="w-3 h-3 text-slate-400" />
          <span className="text-xs text-white/60">
            {brief.homeWeather.temp}° — {brief.homeWeather.description}
          </span>
        </div>
      )}

      {brief.events.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays className="w-3 h-3 text-slate-400" />
          <span className="text-xs text-white/60">
            {brief.events.slice(0, 2).map(event => event.title).join(', ')}
          </span>
        </div>
      )}

      {brief.tasks.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <CheckSquare className="w-3 h-3 text-slate-400" />
          <span className="text-xs text-white/60">
            {t('brief.tasks_due_today', '{{count}} task(s) due today', { count: brief.tasks.length })}
          </span>
        </div>
      )}

      <div className="text-right">
        <span className="text-2xs text-white/30">{brief.date}</span>
      </div>
    </div>
  );
};